import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';
import publicRoutes from '../data/public-routes.json';

export interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const STORAGE_KEY = 'ancelade_lang';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  readonly languages: readonly LanguageOption[] = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', label: 'Português', flag: '🇵🇹' },
    { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
    { code: 'pl', label: 'Polski', flag: '🇵🇱' },
    { code: 'ro', label: 'Română', flag: '🇷🇴' },
  ];

  private get supportedLangs(): string[] {
    return this.languages.map((l) => l.code);
  }

  readonly currentLang = signal('fr');

  isSupported(code: string): boolean {
    return this.supportedLangs.includes(code);
  }

  /** Crawlable equivalent of the current page; untranslated legal pages lead to the locale's home. */
  pathForLang(code: string, url = this.router.url): string {
    if (!this.isSupported(code)) return `/${this.currentLang()}`;
    const segments = url
      .split(/[?#]/, 1)[0]
      .split('/')
      .filter(Boolean)
      .map((part) => part.split(';', 1)[0]);
    if (this.isSupported(segments[0] ?? '')) segments.shift();
    const path = segments.join('/');
    const target = (publicRoutes.aliases as Record<string, string>)[path] ?? path;
    const page = publicRoutes.pages.find((candidate) => candidate.path === target);
    if (!page || (page.languages && !page.languages.includes(code))) return `/${code}`;
    const suffix = target ? `/${target}` : '';
    return `/${code}${suffix}`;
  }

  constructor() {
    this.setLang(this.detectLang());
  }

  private detectLang(): string {
    // Node also exposes navigator; static output must remain deterministic.
    if (!isPlatformBrowser(this.platformId)) return 'fr';
    // Choix utilisateur persiste en priorite
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && this.supportedLangs.includes(stored)) {
        return stored;
      }
    }

    if (typeof navigator === 'undefined') return 'fr';

    // navigator.languages gives ['fr-FR', 'en-US', 'en'] etc.
    const browserLangs = navigator.languages ?? [navigator.language];

    for (const raw of browserLangs) {
      const code = raw.split('-')[0].toLowerCase();
      if (this.supportedLangs.includes(code)) {
        return code;
      }
    }

    // Browser language not supported → English fallback
    return 'en';
  }

  setLang(code: string): void {
    if (!this.supportedLangs.includes(code)) return;
    this.currentLang.set(code);
    this.transloco.setActiveLang(code);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = code;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, code);
    }
  }

  switchLang(code: string): void {
    if (!this.isSupported(code)) return;

    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const segments = currentUrl.split('/').filter(Boolean);
    if (segments.length > 0 && this.isSupported(segments[0])) {
      segments[0] = code;
    } else {
      segments.unshift(code);
    }

    this.setLang(code);
    void this.router.navigate(['/', ...segments], { queryParamsHandling: 'preserve', preserveFragment: true });
  }
}
